/**
 * Light and dark interface themes. The theme is applied as `data-theme` on <html>, which switches the
 * `--ui-*` tokens in tokens.css; canvas code (meters) reads the same tokens through getComputedStyle.
 */
export type Theme = 'dark' | 'light';
export type ThemePreference = 'system' | Theme;

export const THEME_PREFERENCES: readonly ThemePreference[] = ['system', 'dark', 'light'];

export function isTheme(value: unknown): value is Theme {
  return value === 'dark' || value === 'light';
}

export interface ThemeSources {
  /** `#theme=` of the address at load. */
  hash: Theme | null;
  /** The last `omp:theme` message from the embedding portal. */
  parent: Theme | null;
  /** Settings → General. */
  preference: ThemePreference;
  /** `prefers-color-scheme: light` matches; used when the preference is 'system'. */
  systemLight: boolean;
}

/** The hash wins, then the embedding portal, then the preference ('system' follows the OS). */
export function resolveTheme({ hash, parent, preference, systemLight }: ThemeSources): Theme {
  if (hash) return hash;
  if (parent) return parent;
  if (preference === 'system') return systemLight ? 'light' : 'dark';
  return preference;
}

const LIGHT_QUERY = '(prefers-color-scheme: light)';

/** The OS colour-scheme query, or null where matchMedia is missing (old browsers, some test environments). */
export function lightSchemeQuery(): MediaQueryList | null {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(LIGHT_QUERY) : null;
  } catch {
    return null;
  }
}

export function systemPrefersLight(): boolean {
  return lightSchemeQuery()?.matches ?? false;
}

/** Sets `data-theme` and `color-scheme` on the document root so tokens, form controls and scrollbars follow. */
export function applyDocumentTheme(theme: Theme, root: HTMLElement = document.documentElement): void {
  if (root.dataset.theme !== theme) root.dataset.theme = theme;
  root.style.colorScheme = theme;
}

/** The menu-bar logo: white lettering on dark, near-black on light. */
export function logoFor(theme: Theme): string {
  return theme === 'light' ? '/perck-logo-light.png' : '/perck-logo-dark.png';
}
